import type {
  CreativeTemplateCategoryItem,
  CreativeTemplateListReq,
  CreativeTemplateListScope,
  CreativeTemplateResp,
  CreativeTemplateSlotValues,
  CreativeTemplateSortType,
} from '@/views/workspace/board/types/creativeTemplate'

export type {
  CreativeTemplateCategoryItem,
  CreativeTemplateListReq,
  CreativeTemplateListScope,
  CreativeTemplateResp,
  CreativeTemplateSortType,
}

/** 聊天消息状态枚举 */
export const MESSAGE_STATUS = {
  /** 内容审核不通过 */
  AUDIT_FAILED: -2,
  FAILED: -1,
  QUEUING: 1,
  GENERATING: 2,
  COMPLETED: 3,
} as const

export type MessageStatus = (typeof MESSAGE_STATUS)[keyof typeof MESSAGE_STATUS]

export const FAILED_MESSAGE_STATUSES = [
  MESSAGE_STATUS.FAILED,
  MESSAGE_STATUS.AUDIT_FAILED,
] as const

export const isMessageFailedStatus = (status: number | null | undefined): boolean => {
  return status === MESSAGE_STATUS.FAILED || status === MESSAGE_STATUS.AUDIT_FAILED
}

/**
 * 后端一条完整消息结构（列表接口返回的单条记录）
 * 与提交参数 GenerateParams 区分：本条为「消息体」，input/result 为 JSON 字符串
 */
export interface MessageItem {
  id: number
  userId: number
  boardId: number
  clientId: string
  /** 模型标识，如 NANO_BANANA_PRO */
  model: string
  /** 提交参数 JSON 字符串，内容为 GenerateParams 序列化 */
  input: string
  /** 状态：-2:内容审核不通过，-1:失败，1:排队中，2:生成中，3:完成 */
  status: MessageStatus
  /** 生图结果 JSON 字符串 */
  result: string
  userCollectImg?: 0 | 1
  /** module 87（game_ui_split）记录的恢复提示（后端 chatMessage/list 顶层字段） */
  splitRecovery?: SplitRecoveryHint
  utime: string
  ctime: string
}

/** 后端返回的聊天消息结构（与 MessageItem 一致，保留别名兼容现有 API 类型） */
export type ChatMessageResp = MessageItem

/** input JSON 解析后的结构 */
export interface ChatMessageInput {
  prompt: string
  /** 用户原始输入（未经智能强化），用于创作记录展示和重新编辑回填 */
  description?: string
  /** 参考图列表 */
  styleReferList?: Array<{ url: string }>
  /** 参考图列表（新版，直接存储 URL 字符串数组） */
  imageList?: string[]
  /** 全能拆分（module 87）源图：后端写入的拆分输入图，创作记录封面展示用 */
  imageUrl?: string
  /** 全能拆分（module 87）会话所属作品 id：copyResult 副本由后端 createChatMessage 写入
   * 克隆出的新作品 id（≠ 当前画板 artworkId），拆分详情排序/删除/修补按此提交 */
  artworkId?: number
  /** 混元 3D 多视角补充图（展示用，与 imageList 合并为参考图缩略图） */
  multiViewImages?: Array<{ view?: string; imageUrl?: string } | string>
  /** 参考视频列表（视频生视频/多模态生视频等场景） */
  videos?: string[]
  /** 布局参考图 */
  layoutRefer?: {
    id: number
    width: number
    height: number
    image: string
  }
  /** 模型名称 */
  modelName?: string
  /** 模板名称 */
  templateName?: string
  /** 模板预览图 */
  templateCover?: string
  /** 模板是否支持用户输入提示词：0-不支持，1-支持 */
  templateInput?: number
  /** 尺寸比例 */
  aspectRatios?: string
  /** 宽度 */
  width?: number
  /** 高度 */
  height?: number
  /** 图片质量，如 '1K', '2K', '4K' */
  quality?: string
  /** V3.5 精绘/极速画质档 */
  qualityLevel?: string
  /** 附加参数，旧记录可能将画质档位写入其中 */
  attach?: string | Record<string, unknown>
  /** 模型列表 */
  modelList?: Array<{ id: number; modelName: string; preview?: string }>
  /** 风格列表 */
  loraList?: Array<{ modelId: number }>
  /** 是否使用了提示增强 */
  isPromptEnhanced?: boolean
  /** 提示增强来源：template=模板驱动，user=用户主动选择 */
  promptEnhanceSource?: 'template' | 'user'
}

/** result.param 结构（与 GenerateParams 部分字段一致，用于展示/过滤） */
export interface ChatMessageResultParam {
  /** 发起来源，聊天/画廊等 (注意：后端字段拼写为 actionSouce，非 actionSource) */
  actionSouce?: (typeof ACTION_SOURCE)[keyof typeof ACTION_SOURCE]
  [key: string]: unknown
}

/** result JSON 解析后的结构（生图完成后） */
export interface ChatMessageResult {
  /**
   * 作品版本 ID（与 api/chatMessage/list 单条 record.result.versionId 一致）
   * geneDownload 打点入参 artworkVersionId 应优先取此字段
   */
  versionId?: number
  /** 状态：-1:失败，1:排队中，2:生成中，3:完成 */
  status?: number
  /** 生成的图片对象列表（API返回格式） */
  images?: Array<{
    id: number
    image: string
    width: number
    height: number
    size: number
    collect?: 0 | 1
    layerCount?: number
    obj?: string
    /** 3D 模型 GLB 文件 URL（混元 3D 产物；兼容旧字段 glb） */
    glbUrl?: string
    glb?: string
  }>
  /** 生成的图片 URL 列表（兼容旧格式） */
  imageUrls?: string[]
  /** 单张图片 URL（兼容旧格式） */
  image?: string
  /**
   * 生成的视频对象列表（ComfyUI 回调产物：图片 images / 视频 videos / 其他文件 files 分数组下发）
   * 视频地址优先取 mpFour（versionList ArtworkVideo 同构），兼容 video / videoUrl / url 字段
   */
  videos?: Array<{
    id?: number
    mpFour?: string
    video?: string
    videoUrl?: string
    url?: string
    cover?: string
    width?: number
    height?: number
    collect?: number
    [key: string]: unknown
  }>
  /** 其他文件列表（预留：ComfyUI files 产物，如 PSD 源文件） */
  files?: Array<{ id?: number; file?: string; url?: string; [key: string]: unknown }>
  /** 拆分任务（module 87 等）结果清单 URL：背景补全收敛后的权威 manifest（final/revision-N） */
  resultManifestUrl?: string
  /** 生图参数（含 actionSouce 等，用于过滤/展示） */
  param?: ChatMessageResultParam
}

/** module 87（game_ui_split）记录的轻量恢复提示（仅用于判断是否需要恢复，不含完整命令详情） */
export interface SplitRecoveryHint {
  supported: boolean
  resumeRequired: boolean
  activeCommandCount: number
}

/**
 * 前端消息展示用结构
 * 由 MessageItem 转换而来：rawInput = message.input（GenerateParams JSON），input 为解析后的展示用结构
 */
export interface ChatMessage {
  /** 消息 ID（来自 MessageItem.id，本地新建时可为 0） */
  id?: number
  /** 客户端 ID（用于关联前端生图任务） */
  clientId: string
  /** 原始 input JSON（即 MessageItem.input，内容为 GenerateParams 序列化） */
  rawInput: string
  /** 解析后的展示用 input（由 GenerateParams 或 rawInput 解析得到） */
  input: ChatMessageInput
  /** 原始 result JSON */
  rawResult: string
  /** 解析后的 result */
  result: ChatMessageResult | null
  /** 消息状态 */
  status: MessageStatus
  /** 参考图列表（从 input 中提取） */
  referImages: string[]
  /** 生成结果图列表（从 result 中提取） */
  resultImages: string[]
  userCollectImg?: 0 | 1
  backendModel?: string
  /** 模型名称 */
  modelName: string
  /** 尺寸文本 */
  sizeText: string
  /** 比例文本 */
  ratioText: string
  /** 模板名称 */
  templateName: string
  /** 模板预览图 */
  templateCover: string
  /** 分辨率文本（如 '2K'） */
  qualityText: string
  /** 创建时间（来自 MessageItem.ctime） */
  ctime?: string
  /** 更新时间（来自 MessageItem.utime） */
  utime?: string
  /** 拆分结果 manifest 链接（module 87 game_ui_split 等：result 字段存 manifest URL 而非图片 JSON 时写入） */
  manifestUrl?: string
  /** 全能拆分计费场景（module 87：FULL_SPLIT 整图拆分 / EXTRACT_ELEMENT 提取元素 / CONTINUE_SPLIT 继续拆分；由 transformer 从 input.gameUiSplitScene 透传，创作记录据此区分提取与整图拆分） */
  gameUiSplitScene?: string
  /** 拆分恢复提示（module 87 game_ui_split：由 transformer 从记录透传，供创作记录卡显示「恢复」入口） */
  splitRecovery?: SplitRecoveryHint
  /** 排队位置（当前排在第几位） */
  queuePosition?: number
  /** 初始排队人数（用于显示总数） */
  initialQueuePosition?: number
  /** 预计剩余时间（秒） */
  remainingTime?: number
  /** 生图模块ID（用于判断是否为画布模型生成） */
  module?: number
  /** 创作类型（由 transformer 注入，避免组件重复检测） */
  creationType?: 'normal' | 'comfy-single' | 'comfy-split' | 'psd' | 'video' | 'video-frames' | 'zip-package'
}

/** 聊天列表请求参数 */
export interface ChatMessageListReq {
  boardId: number
  pageNo?: number
  pageSize?: number
}

export type { PageResp } from '@/views/workspace/board/types/page'

// ============ 任务同步 ============

/** waitClientIds 接口返回的待处理任务 */
export interface WaitTask {
  id: number
  artworkId: number
  userId: number
  geneType: number
  clientId: string
  promptId: string | null
  module: number
  subModule: string | null
  /** 任务状态：1-排队中，2-生成中 */
  status: number
  source: number
  param: string
  referId: string | null
  styleReferId: string | null
  startTime: string | null
  finishTime: string | null
  ctime: string
  utime: string
  isHigh: number | null
  deleteStatus: number
  ckptType: string
  env: string
  geneMsg: string | null
  isFirst: number | null
}

/** versionList 接口返回的生图版本记录图片 */
export interface VersionImage {
  id: number
  artworkId: number
  versionId: number
  userId: number
  type: number
  image: string
  glb: string | null
  pixeloeImg: string | null
  width: number
  height: number
  size: number
  upload: number
  ctime: string
  utime: string
  redrawWeight: number | null
  deleteStatus: number
  collect: number
  layerCount?: number
  obj?: string
}

/** versionList 接口返回的生图版本记录 */
export interface VersionRecord {
  id: number
  artworkId: number
  images: VersionImage[]
  clientId: string
  promptId: string | null
  module: number
  /** 版本状态：-1-失败，1-排队中，2-生成中，3-完成 */
  status: number
  source: number
  param: string
  referId: string | null
  finishTime: string | null
  ctime: string
  utime: string
  avatar: string | null
  nickname: string | null
  shareType: number
  videoUrl: string | null
  /** 视频产物列表（ComfyUI 视频模板回调产物：mpFour 为视频地址） */
  videos?: Array<{ id?: number; mpFour?: string; video?: string; videoUrl?: string; url?: string; cover?: string; [key: string]: unknown }>
  artworkVersionType: string
}

/** versionList 接口请求参数 */
export interface VersionListReq {
  artworkId: number
  module?: number | null
  pageNo: number
  pageSize: number
}

/** waitClientIds 接口请求参数 */
export interface WaitClientIdsReq {
  artworkId: number
}

// ============ 创意模板 ============
// 列表请求见 @/views/workspace/board/types/creativeTemplate CreativeTemplateListReq

// ============ 生图参数 ============

/** 生图模块枚举 */
export const GENERATE_MODULE = {
  /** NanoBanner */
  NANO_BANNER: 30,
  /** 豆包 Seedream */
  DOUBAO_SEEDREAM: 31,
  /** Klein */
  KLEIN: 32,
} as const

/** 来源枚举 */
export const ACTION_SOURCE = {
  /** 聊天 */
  CHAT: 'chat',
  /** 画廊 */
  GALLERY: 'gallery',
} as const

export type GenerateModule = (typeof GENERATE_MODULE)[keyof typeof GENERATE_MODULE]

export type RefImgSource =
  | 'none'
  | 'local'
  | 'gallery'
  | 'gallery_drag'
  | 'asset_panel'
  | 'chat_panel'
  | 'canvas'
  | 'canvas_quick_edit'
  | 'mixed'
  | string

/**
 * 生图请求参数（提交给生图引擎）
 * 与 MessageItem 区分：本条仅用于「提交」，后端消息的 input 字段即本结构的 JSON 字符串
 * @see constants.DEFAULT_GENERATE_PARAMS
 */
export interface GenerateParams {
  /** 用户id */
  userId: number
  /** 用户唯一标识 */
  uniqueId: string
  /** 任务ID */
  clientId: string
  /** 画板 ID */
  artworkId: number
  /** 使用的是什么模型，NanoBanner 30，doubao Seedream 31 , Klein 32 */
  module: GenerateModule | number
  /** 风格列表, 后台需要，先固定传空数组 */
  loraList: Array<{ modelId: number; weight?: number }>
  /** 1 直接生成 */
  source: number
  /** 会员类型, pro plus free */
  memberType: 'free' | 'pro' | 'plus' | string
  /** 是否使用 VIP 通道 */
  vipChannel: boolean
  /** 尺寸比例，如 '1:1', '16:9' 等；视频模式下不需要此字段 */
  aspectRatios?: string,
  /** 画板 ID */
  boardId: number,
  /**
   * 图片尺寸：
   * - 全能编辑 V1 传字符串如 "2048x2048"
   * - 极速编辑 Lite 传整数 1-9（对应比例枚举）
   */
  size?: string | number
  /** 图片质量，2K, 4K；视频模式下不需要此字段 */
  quality?: '2K' | '4K' | string
  /** V3.5 精绘/极速画质档（后端据此拼 ModelVersion） */
  qualityLevel?: string
  /** 参考图列表,字符串数组 */
  imageList: string[],
  /**
   * 单张参考图 URL：
   * - 混元 3D 图生 3D 使用的单张参考图
   * - MJ 悠船垫图 URL（区别于通用 imageList 数组和 Seedream imageUrls 数组，最多 1 项）
   */
  imageUrl?: string
  /** 使用的创意模板（可为接口返回的 CreativeTemplateResp 或前端选择的 TemplateInfo） */
  _creativeTemplate?: CreativeTemplateResp | { id: number; name: string; coverUrl?: string; preview?: string; prompt?: string; description?: string } | null,
  /** 发起来源 (注意：后端字段拼写为 actionSouce，非 actionSource) */
  actionSouce?: typeof ACTION_SOURCE[keyof typeof ACTION_SOURCE],
  /** 提示词 */
  prompt: string
  /** 附加信息,字符串对象 */
  attach?: string
  /**
   * 极速编辑 Lite 输入图数量控制
   * 1=1张图, 2=2张图, 3=3张图, 4=0张图（纯文生图）
   */
  styleId?: number

  /** ComfyUI 模板下发的生图算力（对应模板 points 字段） */
  tmplPoint?: number
  /** ComfyUI 模板下发的预计生成耗时（对应模板 waitTime 字段） */
  tmplTime?: number

  canvas_trigger?: string
  is_regenerate?: boolean
  is_prompt_modified?: boolean
  ref_img_source?: RefImgSource
  canvas_total_imgs?: number

  videoRatio?: string
  videoResolution?: string | number
  videoDuration?: number
  /** 参考视频 URL 列表（多模态视频模块与 ComfyUI 视频模板工作流入参共用） */
  videos?: string[]
  /** promptSlots 槽位 ID → 用户填写值。顶层字段直传后端 GeneTaskReq.slotValues，ComfyUI 模板 nodeAssociations 仅从顶层回填（缺失时静默回退控件默认值）；attach._slotValues 仅存档供重新编辑回填，后端不解析。switch 开关控件值为原生布尔 true/false */
  slotValues?: CreativeTemplateSlotValues

  // ============ Seedream V5 Pro（module 74）专用字段 ============
  /** 输出比例（Service 参数面为单数命名，区别于通用 aspectRatios），如 '16:9' */
  aspectRatio?: string
  /** 分辨率档位：'1k' | '2k'（区别于通用 quality 的 '1K'/'2K'） */
  resolution?: string
  /** 图生图输入图 URL（1-6 张，Service 参数面，区别于通用 imageList） */
  imageUrls?: string[]
  /** 输出像素宽，按官方尺寸表换算（后端 RunninghubSeedreamVo 必填） */
  width?: number
  /** 输出像素高，按官方尺寸表换算（后端 RunninghubSeedreamVo 必填） */
  height?: number
  // ============ MJ 悠船文生图参数 ============
  /**
   * 悠船其他参数拼接串（仅悠船）：面板参数 + 手写 --xxx。
   * 不含模型/垫图/比例（分别走 module / imageUrl / aspectRatio）。
   */
  youchuanParams?: string
  /** 选中的 general_template 主键。仅 MJ 模板生成时写入，供服务端统计 / 最近使用 */
  generalTemplateId?: number
  /** 原生 2K 开关；悠船提交不再传，由 module 表达 */
  hd?: boolean
  /** 是否使用了模板（当为 true 时，powerCost 才有效） */
  hasTemplate?: boolean
  /** 本次任务消耗算力（基础算力 + promptSlots 选项 cost 累加） */
  powerCost?: number
  /** 全能编辑 V3 透明背景：开启时为 "transparent" */
  imgBackground?: string
}
